import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../services/global-environment';
import { law_unfit_list } from '../models/regulation-unfit-item.model';


@Injectable({
  providedIn: 'root'
})
export class EmailService {
  private apiUrl = `${environment.apiUrl}/api/email/sendEmail`; // API URL for sending emails

  constructor(private http: HttpClient) {}

  /**
   * Send notification email for regulation tracking items
   * @param recipientEmail - Email address of recipient
   * @param recipientName - Name of the recipient
   * @param regulationName - Name of the regulation
   * @param articleDesc - Description of the article
   * @param dueDate - Due date for the task
   * @param taskUrl - URL to access the task
   */
  sendRegulationTrackingNotification(
    recipientEmail: string,
    recipientName: string,
    item: law_unfit_list,
    dueDate: string,
    taskUrl?: string
  ): Observable<any> {

    //  console.log("recipientEmail:",recipientEmail)
    //  console.log("recipientName:",recipientName)
    //   console.log("item:",item)
    //   console.log("dueDate:",dueDate)
    //   console.log("taskUrl:",taskUrl)


    const subject = `【合規助理-法規改善事項】改善事項通知 - 案件預計結案日：${dueDate}`;

    // 使用 HTML 格式優化 email 樣式
    const body = `
<div style="font-family: 'Segoe UI', Arial, '微軟正黑體', sans-serif; color: #222;">
  <p style="font-size: 1.1em;">${recipientName} 您好：</p>
  <p>您在合規助理系統中被指派以下法規改善事項，請於案件結案日之前完成相關處理：</p>
  <table style="border-collapse: collapse; margin: 16px 0;">
    <tr>
      <td style="padding: 6px 12px;  font-weight: bold;">法規名稱</td>
      <td style="padding: 6px 12px; ">${item.law_name}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px;  font-weight: bold;">條次</td>
      <td style="padding: 6px 12px; ">${item.article_no}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px;  font-weight: bold;">條文說明</td>
      <td style="padding: 6px 12px; ">${item.article_content}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px;  font-weight: bold;">現況說明</td>
      <td style="padding: 6px 12px; ">${item.current_status}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px;  font-weight: bold;">預計結案日</td>
      <td style="padding: 6px 12px; ">${dueDate}</td>
    </tr>
  </table>
  <p>
    <a href="${taskUrl || '#'}" style="display: inline-block; padding: 10px 18px; background: #1976d2; color: #fff; text-decoration: none; border-radius: 4px; font-weight: bold;">
      👉 點擊此處進入改善事項填寫頁面
    </a>
  </p>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    如有任何疑問，請聯繫相關負責窗口，謝謝。
  </p>
</div>
    `;

    return this.http.post(this.apiUrl, {
      from: "JSAI 系統通知",
      to: recipientEmail,
      subject: subject,
      body: body,
      isHtml: true // 新增此參數以標示為 HTML 郵件
    });
  }

  /**
   * Send compliance assignment notification email
   * @param recipientEmail - Email address of recipient
   * @param recipientName - Name of the recipient
   * @param taskInfo - Task information object
   */
  sendComplianceAssignmentNotification(
    recipientEmail: string,
    recipientName: string,
    taskInfo: {
      taskName: string;
      regulationCount: number;
      articleCount: number;
      notes: string;
      startDate: string;
      deadline: string;
    }
  ): Observable<any> {
    const subject = `【合規助理-合規性判斷】任務指派通知 - 任務截止日：${taskInfo.deadline}`;

    // 使用 HTML 格式優化 email 樣式
    const body = `
<div style="font-family: 'Segoe UI', Arial, '微軟正黑體', sans-serif; color: #222;">
  <p style="font-size: 1.1em;">［${recipientName}］您好：</p>
  <p>您在合規助理系統中被指派以下任務需進行法條符合性判別，請於任務截止日之前完成相關處理：</p>
  <table style="border-collapse: collapse; margin: 16px 0;">
    <tr>
      <td style="padding: 6px 12px; font-weight: bold;">查核主題：</td>
      <td style="padding: 6px 12px;">${taskInfo.taskName}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; font-weight: bold;">法規數/法條數：</td>
      <td style="padding: 6px 12px;">${taskInfo.regulationCount} / ${taskInfo.articleCount}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; font-weight: bold;">備註：</td>
      <td style="padding: 6px 12px;">${taskInfo.notes || '無'}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; font-weight: bold;">執行開始日期：</td>
      <td style="padding: 6px 12px;">${taskInfo.startDate}</td>
    </tr>
    <tr>
      <td style="padding: 6px 12px; font-weight: bold;">任務截止日期：</td>
      <td style="padding: 6px 12px;">${taskInfo.deadline}</td>
    </tr>
  </table>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    如有任何疑問，請聯繫相關負責窗口，謝謝。
  </p>
</div>
    `;

    return this.http.post(this.apiUrl, {
      from: "JSAI 系統通知",
      to: recipientEmail,
      subject: subject,
      body: body,
      isHtml: true
    });
  }

  /**
   * Send approval notification email to next approvers
   * @param recipientEmails - Array of email addresses
   * @param approvalInfo - Approval information object
   * @param locale - Language locale for email content
   */
  sendApprovalNotification(
    recipientEmails: string[],
    approvalInfo: {
      formName: string;
      flowName: string;
      currentStepName: string;
      currentStepLevel: number;
      nextStepName: string;
      nextStepLevel: number;
      previousApproverName: string;
      previousApprovalResult: string; // 固定為 "同意/承認/Approved"
      taskUrl: string;
      deadline?: string;
      companyId?: string;
    },
    locale: 'zh-TW' | 'ja-JP' | 'en-US' = 'zh-TW'
  ): Observable<any> {
    // 驗證輸入
    if (!recipientEmails || recipientEmails.length === 0) {
      return new Observable(observer => {
        observer.error(new Error('No recipient emails provided'));
      });
    }

    if (!approvalInfo || !approvalInfo.formName) {
      return new Observable(observer => {
        observer.error(new Error('Approval information is incomplete'));
      });
    }

    const template = this.getEmailTemplate(locale);
    const subject = template.subject.replace('{formName}', approvalInfo.formName);

    // 為每個收件人發送個別郵件
    const emailPromises = recipientEmails.map(email => {
      // 驗證 email 格式
      if (!email || !email.includes('@')) {
        console.warn('Invalid email address:', email);
        return Promise.resolve({ success: false, email, error: 'Invalid email format' });
      }

      const recipientName = email.split('@')[0]; // 簡單從 email 取得名稱，實際應該從用戶資料取得
      const body = this.generateApprovalEmailBody(template, approvalInfo, recipientName);

      return this.http.post(this.apiUrl, {
        from: "JSAI 系統通知",
        to: email,
        subject: subject,
        body: body,
        isHtml: true
      }).toPromise().catch(error => {
        console.error(`Failed to send email to ${email}:`, error);
        return { success: false, email, error: error.message || 'Send failed' };
      });
    });

    // 使用 Promise.all 等待所有郵件發送完成，然後轉換為 Observable
    return new Observable(observer => {
      Promise.all(emailPromises)
        .then(results => {
          const successful = results.filter(result => result && (result as any).success !== false);
          const failed = results.filter(result => result && (result as any).success === false);
          
          observer.next({ 
            success: true, 
            results,
            totalSent: successful.length,
            totalFailed: failed.length,
            failedEmails: failed.map(f => (f as any).email || 'unknown')
          });
          observer.complete();
        })
        .catch(error => {
          console.error('Error sending approval notifications:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Generate approval email body content
   */
  private generateApprovalEmailBody(
    template: any,
    approvalInfo: any,
    recipientName: string
  ): string {
    return `
<div style="font-family: 'Segoe UI', Arial, '微軟正黑體', sans-serif; color: #222;">
  <p style="font-size: 1.1em;">${template.greeting.replace('{recipientName}', recipientName)}</p>
  <p>${template.notification}</p>
  <table style="border-collapse: collapse; margin: 16px 0; border: 1px solid #ddd;">
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.formName}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.formName}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.flowName}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.flowName}</td>
    </tr>
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.currentStep}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.nextStepName}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.stepLevel}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${template.labels.stepLevel} ${approvalInfo.nextStepLevel}</td>
    </tr>
    ${approvalInfo.deadline ? `
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.deadline}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.deadline}</td>
    </tr>
    ` : ''}
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.previousApprover}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd;">${approvalInfo.previousApproverName}</td>
    </tr>
    <tr style="background-color: #f9f9f9;">
      <td style="padding: 8px 12px; border: 1px solid #ddd; font-weight: bold;">${template.labels.previousResult}</td>
      <td style="padding: 8px 12px; border: 1px solid #ddd; color: #2e9116; font-weight: bold;">${template.values.approved}</td>
    </tr>
  </table>
  <p style="margin: 20px 0;">
    <a href="${approvalInfo.taskUrl}" style="display: inline-block; padding: 12px 24px; background: #f5820d; color: #fff; text-decoration: none; border-radius: 6px; font-weight: bold;">
      ${template.button}
    </a>
  </p>
  <p style="margin-top: 24px; color: #888; font-size: 0.95em;">
    ${template.footer}
  </p>
</div>
    `;
  }

  /**
   * Get email template based on locale
   */
  private getEmailTemplate(locale: 'zh-TW' | 'ja-JP' | 'en-US') {
    switch (locale) {
      case 'ja-JP':
        return {
          subject: '【承認通知】{formName} - 承認をお待ちしております',
          greeting: '{recipientName} 様',
          notification: '承認待ちの書類がございます。期限内に処理をお願いいたします：',
          labels: {
            formName: 'フォーム名',
            flowName: '承認フロー',
            currentStep: '承認待ちステップ',
            stepLevel: '承認段階',
            deadline: '承認期限',
            previousApprover: '前段階承認者',
            previousResult: '前段階結果',
            approvalStatus: '承認状況'
          },
          values: {
            approved: '承認'
          },
          button: '👉 こちらをクリックして承認',
          footer: 'ご不明な点がございましたら、担当窓口までお問い合わせください。'
        };
      case 'en-US':
        return {
          subject: '[Approval Required] {formName} - Awaiting Your Approval',
          greeting: 'Dear {recipientName},',
          notification: 'You have a document pending approval. Please complete the process within the deadline:',
          labels: {
            formName: 'Form Name',
            flowName: 'Approval Flow',
            currentStep: 'Pending Step',
            stepLevel: 'Approval Level',
            deadline: 'Deadline',
            previousApprover: 'Previous Approver',
            previousResult: 'Previous Result',
            approvalStatus: 'Approval Status'
          },
          values: {
            approved: 'Approved'
          },
          button: '👉 Click here to approve',
          footer: 'If you have any questions, please contact the relevant personnel.'
        };
      case 'zh-TW':
      default:
        return {
          subject: '【簽核通知】{formName} - 待您簽核',
          greeting: '{recipientName} 您好：',
          notification: '您有一份文件待簽核，請於期限內完成相關處理：',
          labels: {
            formName: '表單名稱',
            flowName: '簽核流程',
            currentStep: '待簽核步驟',
            stepLevel: '簽核階段',
            deadline: '簽核期限',
            previousApprover: '前一關簽核人',
            previousResult: '前一關結果',
            approvalStatus: '簽核狀態'
          },
          values: {
            approved: '同意'
          },
          button: '👉 點擊此處進行簽核',
          footer: '如有任何疑問，請聯繫相關負責窗口，謝謝。'
        };
    }
  }
}
